import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider, LinearProgress, Avatar, Switch, Tooltip } from "@mui/material";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2BarIcon from "@mui/icons-material/SignalCellularAlt2Bar";
import SignalCellularAlt1BarIcon from "@mui/icons-material/SignalCellularAlt1Bar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { ConnectionQuality, VideoResolution, LocalRecordingErrorCode } from "./ParticipantStatusService";

function getLocalRecordingErrorLabel(code?: LocalRecordingErrorCode | string): string | null {
    const trimmed = (code ?? '').trim();
    if (trimmed === '') return null;
    if (trimmed === 'start_failed') return "Local recording couldn't be started";
    if (trimmed === 'fatal') return 'Local recording failed';
    return 'Local recording issue';
}

export interface Participant {
    id: string; // participantIdentifier
    name: string;
    isHost?: boolean;
    connectionQuality: ConnectionQuality;
    videoResolution?: VideoResolution; // '480p', '720p', '1080p', '1440p', '4K'
    uploadProgress?: number; // 0-100, undefined if not uploading
    uploadComplete?: boolean;
    noiseCancellationEnabled?: boolean;
    localRecordingError?: string; // present when the client's local recording failed
}

interface StudioPeopleSidebarProps {
    participants?: Participant[];
    recordingInProgress?: boolean;
    recordingStopped?: boolean; // True when recording stopped but uploads may still be in progress
    hostConnectionQuality?: ConnectionQuality;
    hostName?: string;
    hostUploadProgress?: number; // Host's own upload progress (0-100)
    hostUploadComplete?: boolean;
    hostNoiseCancellationEnabled?: boolean;
    hostLocalRecordingError?: string;
    onToggleNoiseCancellation?: (participantId: string, enabled: boolean) => void;
    onToggleHostNoiseCancellation?: (enabled: boolean) => void;
}

const titleTextStyle = {
    color: 'var(--app-text-primary)',
    paddingLeft: '8px',
    paddingRight: '8px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    fontFamily: 'Inter, sans-serif',
    userSelect: 'none' as const,
};

const ConnectionIndicator: React.FC<{ quality: ConnectionQuality }> = ({ quality }) => {
    const getColor = () => {
        if (quality === 'excellent' || quality === 'good') return '#22C55E';
        if (quality === 'fair') return '#F59E0B';
        if (quality === 'poor') return '#EF4444';
        return '#9CA3AF';
    };
    
    const getLabel = () => {
        if (quality === 'excellent') return 'Excellent';
        if (quality === 'good') return 'Good';
        if (quality === 'fair') return 'Fair';
        if (quality === 'poor') return 'Poor';
        return 'Unknown';
    };
    
    const Icon = (quality === 'excellent' || quality === 'good')
        ? SignalCellularAltIcon 
        : quality === 'fair' 
            ? SignalCellularAlt2BarIcon 
            : SignalCellularAlt1BarIcon;
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Icon sx={{ fontSize: 16, color: getColor() }} />
            <Typography sx={{ 
                fontSize: 13, 
                color: 'var(--app-text-muted)',
                fontFamily: 'Inter, sans-serif',
            }}>
                {getLabel()}
            </Typography>
            {/* Connection status dot */}
            <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getColor(),
                ml: 'auto',
            }} />
        </Box>
    );
};

interface ParticipantCardProps {
    participant: Participant;
    participantIndex: number;
    showUpload: boolean;
    recordingStopped: boolean; // Only show "complete" state after recording stops
    onToggleNoiseCancellation?: (participantId: string, enabled: boolean) => void;
    onToggleHostNoiseCancellation?: (enabled: boolean) => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({ 
    participant, 
    showUpload,
    recordingStopped,
    onToggleNoiseCancellation,
    onToggleHostNoiseCancellation,
    participantIndex,
}) => {
    const localRecordingErrorLabel = getLocalRecordingErrorLabel(participant.localRecordingError);

    const handleNoiseCancellationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (participant.isHost && onToggleHostNoiseCancellation) {
            onToggleHostNoiseCancellation(event.target.checked);
        } else if (onToggleNoiseCancellation) {
            onToggleNoiseCancellation(participant.id, event.target.checked);
        }
    };

    // Show toggle if it's host with handler, or non-host with handler
    const showNoiseCancellationToggle = 
        (participant.isHost && onToggleHostNoiseCancellation) || 
        (!participant.isHost && onToggleNoiseCancellation);
    
    // Only show "complete" state after recording has stopped
    // During recording, even if progress is 100%, show as "uploading" since more chunks will come
    const showAsComplete = recordingStopped && participant.uploadComplete;
    
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                backgroundColor: 'var(--app-bg-surface)',
                border: '1px solid var(--app-border-subtle)',
                overflow: 'hidden',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                    borderColor: 'var(--app-border-muted)',
                },
            }}
        >
            {/* Main participant info */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
            }}>
                {/* Numbered avatar */}
                <Avatar
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#3B82F6',
                        fontSize: 18,
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
                    }}
                >
                    {participantIndex}
                </Avatar>
                
                {/* Name and connection */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: 'var(--app-text-primary)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontFamily: 'Inter, sans-serif',
                            }}
                        >
                            {participant.name}
                        </Typography>
                        {participant.isHost && (
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: '#DBEAFE',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: '#2563EB',
                                    fontFamily: 'Inter, sans-serif',
                                }}
                            >
                                Host
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConnectionIndicator quality={participant.connectionQuality} />
                        {/* Video resolution indicator for non-hosts */}
                        {!participant.isHost && participant.videoResolution && (
                            <Tooltip title="Video resolution" arrow placement="top">
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--app-bg-subtle)',
                                }}>
                                    <VideocamOutlinedIcon sx={{ fontSize: 14, color: 'var(--app-text-muted)' }} />
                                    <Typography sx={{ 
                                        fontSize: 11, 
                                        fontWeight: 500,
                                        color: 'var(--app-text-muted)',
                                        fontFamily: 'Inter, sans-serif',
                                    }}>
                                        {participant.videoResolution}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>
            
            {/* Noise Cancellation Toggle */}
            {showNoiseCancellationToggle && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderTop: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                }}>
                    <Typography sx={{ 
                        fontSize: 13, 
                        color: 'var(--app-text-primary)',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                    }}>
                        Noise Cancellation
                    </Typography>
                    <Switch
                        checked={participant.noiseCancellationEnabled ?? false}
                        onChange={handleNoiseCancellationToggle}
                        sx={{
                            width: 44,
                            height: 24,
                            padding: 0,
                            '& .MuiSwitch-switchBase': {
                                padding: 0,
                                margin: '2px',
                                transitionDuration: '200ms',
                                '&.Mui-checked': {
                                    transform: 'translateX(20px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: '#2B6BFD',
                                        opacity: 1,
                                        border: 0,
                                    },
                                },
                            },
                            '& .MuiSwitch-thumb': {
                                boxSizing: 'border-box',
                                width: 20,
                                height: 20,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            },
                            '& .MuiSwitch-track': {
                                borderRadius: 12,
                                backgroundColor: 'var(--app-border-subtle)',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Local recording error */}
            {!!localRecordingErrorLabel && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderTop: '1px solid var(--app-border-subtle)',
                        backgroundColor: '#FEF2F2',
                        borderLeft: '3px solid #EF4444',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: '#991B1B',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                        }}
                        title={participant.localRecordingError}
                    >
                        {localRecordingErrorLabel}
                    </Typography>
                </Box>
            )}
            
            {/* Upload progress - shown during/after recording */}
            {showUpload && participant.uploadProgress !== undefined && (
                <Box sx={{ 
                    px: 2, 
                    py: 1.5, 
                    borderTop: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography sx={{ 
                            fontSize: 13, 
                            color: 'var(--app-text-primary)',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                        }}>
                            {showAsComplete ? 'Upload complete' : 'Uploading...'}
                        </Typography>
                        {showAsComplete ? (
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} />
                        ) : (
                            <Typography sx={{ 
                                fontSize: 13, 
                                color: 'var(--app-text-muted)',
                                fontFamily: 'Inter, sans-serif',
                            }}>
                                {Math.round(participant.uploadProgress)}%
                            </Typography>
                        )}
                    </Box>
                    {!showAsComplete && (
                        <LinearProgress
                            variant="determinate"
                            value={participant.uploadProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'var(--app-border-subtle)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#3B82F6',
                                    borderRadius: 3,
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

const StudioPeopleSidebar: React.FC<StudioPeopleSidebarProps> = ({ 
    participants = [],
    recordingInProgress = false,
    recordingStopped = false,
    hostConnectionQuality = 'good',
    hostName = 'You (Host)',
    hostUploadProgress,
    hostUploadComplete = false,
    hostNoiseCancellationEnabled = false,
    hostLocalRecordingError,
    onToggleNoiseCancellation,
    onToggleHostNoiseCancellation,
}) => {
    // Build display list: host first, then other participants
    const hostParticipant: Participant = {
        id: 'host',
        name: hostName,
        isHost: true,
        connectionQuality: hostConnectionQuality,
        noiseCancellationEnabled: hostNoiseCancellationEnabled,
        uploadProgress: hostUploadProgress,
        uploadComplete: hostUploadComplete,
        localRecordingError: hostLocalRecordingError,
    };
    
    const displayParticipants = [hostParticipant, ...participants.filter(p => !p.isHost)];

    // Show upload progress during recording (chunks uploading in real-time) and after recording stops
    const showUpload = recordingInProgress || recordingStopped;

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '8px', gap: 1, marginTop: 1 }}>
                <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PeopleOutlinedIcon sx={{ fontSize: 20, color: 'var(--app-text-primary)' }} />
                </Box>
                <Box sx={titleTextStyle}>People</Box>
                <Box
                    sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: '10px',
                        backgroundColor: 'var(--app-bg-subtle)',
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--app-text-muted)',
                        mr: 1,
                    }}
                >
                    {displayParticipants.length}
                </Box>
            </Box>

            <Divider sx={{ mx: 1 }} />

            {/* Participants list */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: 1,
                    pb: 1,
                    flex: 1,
                    overflow: 'auto',
                }}
            >
                {displayParticipants.map((participant, index) => (
                    <ParticipantCard
                        key={participant.id}
                        participant={participant}
                        participantIndex={index + 1}
                        showUpload={showUpload}
                        recordingStopped={recordingStopped}
                        onToggleNoiseCancellation={onToggleNoiseCancellation}
                        onToggleHostNoiseCancellation={onToggleHostNoiseCancellation}
                    />
                ))}
            </Box>

            {/* Recording/uploading status indicator */}
            {(recordingInProgress || recordingStopped) && (
                <Box
                    sx={{
                        mx: 1,
                        mb: 1,
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: recordingStopped ? '#FEF3C7' : '#FEF2F2',
                        border: `1px solid ${recordingStopped ? '#FCD34D' : '#FECACA'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: recordingStopped ? '#F59E0B' : '#EF4444',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: recordingStopped ? '#92400E' : '#991B1B', 
                        fontWeight: 500 
                    }}>
                        {recordingStopped ? 'Uploading recordings...' : 'Recording in progress'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default StudioPeopleSidebar;
